"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import CssBaseline from "@mui/material/CssBaseline";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LicenseInfo } from "@mui/x-license";
import { es, enUS } from "date-fns/locale";
import { useLocale } from "next-intl";
import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import ThemeModeProvider from "@/theme/ThemeModeProvider";
import type { ThemeMode } from "@/theme/theme-config";
import dynamic from "next/dynamic";
import { createQueryPersister } from "@/lib/offline/query-persister";

// Registrar licencia MUI X (Premium perpetual). Una sola vez al cargar el bundle.
const MUI_X_LICENSE = process.env.NEXT_PUBLIC_MUI_X_LICENSE_KEY;
if (MUI_X_LICENSE) {
  LicenseInfo.setLicenseKey(MUI_X_LICENSE);
}

// Carga dinámica para evitar errores de SSR con google-oauth
const GoogleOAuthProvider = dynamic(
  () => import("@react-oauth/google").then((m) => ({ default: m.GoogleOAuthProvider })),
  { ssr: false }
);

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? "";
const OFFLINE_ENABLED = process.env.NEXT_PUBLIC_OFFLINE_MODE === "1";
const BUILD_ID = process.env.NEXT_PUBLIC_BUILD_ID ?? "1";

function buildQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // `staleTime: 0` → cada vez que un componente se monta (al
        // navegar entre páginas) o se invalida una query, React Query
        // hace fetch al backend. Es la única forma de garantizar que
        // tras "Guardar" siempre veas los datos persistidos, sin que
        // el cache sirva versiones antiguas. El server es rápido y la
        // sensación de "no se ha guardado" cuando sí se guardó es peor
        // que un poco más de tráfico HTTP.
        staleTime: 0,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        // Refetcheamos al re-montar para que volver con el botón
        // "Atrás" del navegador o el "Volver" de la app traiga datos
        // frescos en vez de cache stale.
        refetchOnMount: "always",
        // Solo aplica `networkMode` cuando el modo offline está activo:
        // `offlineFirst` sirve la cache cuando no hay red, evitando que
        // las queries queden en estado `loading` indefinidamente.
        ...(OFFLINE_ENABLED ? { networkMode: "offlineFirst" as const } : {}),
      },
      mutations: {
        // Mutations: `always` corre el `mutationFn` aunque navigator.onLine
        // sea false — el axios interceptor (Fase 7) decidirá si encolar
        // en el outbox o enviar al servidor.
        ...(OFFLINE_ENABLED ? { networkMode: "always" as const } : {}),
      },
    },
  });
}

/// Hook que escucha el evento `outbox:sent` (lo emite outbox.ts cuando
/// el sync engine confirma una mutación con el servidor) y invalida la
/// query root del `entityType` para refetch del estado canónico.
function useClientStateClearListener(queryClient: QueryClient) {
  // Listener que SIEMPRE corre (flag OFF u ON). Cuando el authStore
  // emite `boosters-ai:client-state-cleared` tras logout, vaciamos la
  // cache de React Query en memoria para que la siguiente carga vea
  // estado limpio y no renderice datos de la sesión anterior (PII en
  // dispositivos compartidos).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onCleared = () => queryClient.clear();
    window.addEventListener("boosters-ai:client-state-cleared", onCleared);
    return () =>
      window.removeEventListener("boosters-ai:client-state-cleared", onCleared);
  }, [queryClient]);
}

function useOutboxSyncedInvalidation(queryClient: QueryClient) {
  useEffect(() => {
    if (!OFFLINE_ENABLED) return;
    if (typeof window === "undefined") return;

    const onSync = (e: Event) => {
      const detail = (e as CustomEvent<{ entityType?: string }>).detail;
      const entityType = detail?.entityType;
      if (!entityType) return;
      // Invalida la query root del entityType (eg. `['users']`).
      // Service-level hooks deberían usar root keys consistentes.
      queryClient.invalidateQueries({ queryKey: [entityType] });
    };

    window.addEventListener("outbox:sent", onSync);
    return () => window.removeEventListener("outbox:sent", onSync);
  }, [queryClient]);
}

function QueryWrapperPersisted({ children }: { children: React.ReactNode }) {
  // Construimos persister Y queryClient una sola vez. El persister se
  // suscribe al QueryCache para serializar tras cada cambio.
  const [queryClient] = useState(buildQueryClient);
  const [persister] = useState(createQueryPersister);

  useOutboxSyncedInvalidation(queryClient);
  useClientStateClearListener(queryClient);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 24 * 60 * 60 * 1000, // 24h
        buster: BUILD_ID,
      }}
    >
      <Inner>{children}</Inner>
    </PersistQueryClientProvider>
  );
}

function QueryWrapperBasic({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(buildQueryClient);
  useClientStateClearListener(queryClient);
  return (
    <QueryClientProvider client={queryClient}>
      <Inner>{children}</Inner>
    </QueryClientProvider>
  );
}

// Mapea el locale de next-intl al locale de date-fns que usan los date-pickers
// de MUI. Al cambiar el idioma, los selectores de fecha se localizan también.
const DATE_FNS_LOCALES = { es, en: enUS } as const;

function Inner({ children }: { children: React.ReactNode }) {
  const locale = useLocale();
  const adapterLocale = DATE_FNS_LOCALES[locale as keyof typeof DATE_FNS_LOCALES] ?? es;
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
      <CssBaseline />
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
      <Toaster richColors position="top-center" />
    </LocalizationProvider>
  );
}

function QueryWrapper({ children }: { children: React.ReactNode }) {
  // `OFFLINE_ENABLED` se resuelve en build time (NEXT_PUBLIC_*) → el
  // bundle entero ramifica una sola vez al cargar y el tree-shaker
  // elimina el código no usado en cada build.
  return OFFLINE_ENABLED ? (
    <QueryWrapperPersisted>{children}</QueryWrapperPersisted>
  ) : (
    <QueryWrapperBasic>{children}</QueryWrapperBasic>
  );
}

export default function Providers({
  children,
  initialTheme,
}: {
  children: React.ReactNode;
  initialTheme: ThemeMode;
}) {
  const tree = GOOGLE_CLIENT_ID ? (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <QueryWrapper>{children}</QueryWrapper>
    </GoogleOAuthProvider>
  ) : (
    <QueryWrapper>{children}</QueryWrapper>
  );

  return <ThemeModeProvider initial={initialTheme}>{tree}</ThemeModeProvider>;
}
